import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as auth from '../api/auth';
import useAuthStore from '../stores/authStore';
import { useToast } from '../components/ToastProvider';

const SocialLoginCallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { provider } = useParams(); 
  const queryClient = useQueryClient();
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const { toast } = useToast();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const loginProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (error || !isLoading || loginProcessedRef.current) {
      return;
    }

    loginProcessedRef.current = true;

    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    console.log(`Starting ${provider} OAuth callback processing...`);

    // Handle OAuth errors first
    if (oauthError) {
      console.error('OAuth error:', oauthError, errorDescription);
      setError(errorDescription || t('social_login.errors.oauth_error', { error: oauthError }));
      setIsLoading(false);
      setTimeout(() => navigate('/login'), 5000);
      return;
    }

    if (!code) {
      console.error('Missing authorization code in callback');
      setError(t('social_login.errors.missing_code'));
      setIsLoading(false);
      setTimeout(() => navigate('/login'), 5000);
      return;
    }

    // Validate state parameter for CSRF protection
    const stateKey = `social_login_state_${provider}`;
    const savedState = sessionStorage.getItem(stateKey);

    if (!state) {
      console.error('Missing state parameter in callback');
      setError(t('social_login.errors.missing_state'));
      setIsLoading(false);
      setTimeout(() => navigate('/login'), 5000);
      return;
    }

    if (!savedState) {
      console.error('No saved state found in session storage for key:', stateKey);
      setError(t('social_login.errors.invalid_state'));
      setIsLoading(false);
      setTimeout(() => navigate('/login'), 5000);
      return;
    }

    if (state !== savedState) {
      console.error('State mismatch:', { received: state, expected: savedState });
      setError(t('social_login.errors.state_mismatch'));
      setIsLoading(false);
      setTimeout(() => navigate('/login'), 5000);
      return;
    }

    // Clear the state from session storage after successful validation
    sessionStorage.removeItem(stateKey);
    console.log(`State validation successful for ${provider}, proceeding with login`);

    const handleSocialLogin = async () => {
      try {
        console.log(`Processing ${provider} login with authorization code...`);

        // For LINE and Facebook login, pass the callback URL as well
        const callbackUrl = provider.toLowerCase() === 'line'
          ? `${window.location.origin}/auth/line/callback`
          : provider.toLowerCase() === 'facebook'
          ? `${window.location.origin}/auth/facebook/callback`
          : null;

        const response = await auth.socialLogin(provider, code, callbackUrl);

        console.log(`${provider} login successful, setting access token...`);
        setAccessToken(response.accessToken);
        await queryClient.invalidateQueries({ queryKey: ['user-profile'] });

        // Show success message
        toast(t('social_login.success'));

        // Check if there's a redirect URL stored before login
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl);
        } else {
          navigate('/');
        }
      } catch (err) {
        console.error(`${provider} login failed:`, err);
        const errorMessage = err.response?.data?.message || t('social_login.errors.general');
        setError(errorMessage);
        setIsLoading(false);
        // Navigate back to login on failure after a delay
        setTimeout(() => navigate('/login'), 5000);
      }
    };

    handleSocialLogin();
  }, [location.search, provider]); // Only depend on URL search params and provider

  if (error) {
    return (
      <div className="container py-8 text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">{t('social_login.errors.title')}</h1>
        <p>{error}</p>
        <p>{t('social_login.errors.redirecting')}</p>
      </div>
    );
  }

  // Show loading state (either initial loading or processing login)
  return (
    <div className="container py-8 text-center">
      <p>{isLoading ? t('social_login.loading') : t('social_login.verifying')}</p>
      {/* You could add a spinner here */}
    </div>
  );
};

export default SocialLoginCallbackPage; 